import { Suspense, lazy, useEffect } from 'react'
import { Routes, Route, Navigate, useParams, useLocation, Outlet } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import {
  supportedLanguages,
  defaultLanguage,
  type SupportedLanguage,
} from '@/i18n'
import { localizedRoutes } from '@/i18n/routes'
import { Header } from '@/components/layout'

// Lazy-loaded page components for code splitting
const HomePage = lazy(() => import('@/pages/HomePage'))
const AboutPage = lazy(() => import('@/pages/AboutPage'))
const ProductsPage = lazy(() => import('@/pages/ProductsPage'))
const SustainabilityPage = lazy(() => import('@/pages/SustainabilityPage'))
const NewsPage = lazy(() => import('@/pages/NewsPage'))
const NewsDetailPage = lazy(() => import('@/pages/NewsDetailPage'))
const ContactsPage = lazy(() => import('@/pages/ContactsPage'))
const CookiePolicyPage = lazy(() => import('@/pages/CookiePolicyPage'))
const PrivacyPage = lazy(() => import('@/pages/PrivacyPage'))
const WhistleblowingPage = lazy(() => import('@/pages/WhistleblowingPage'))
const NotFoundPage = lazy(() => import('@/pages/NotFoundPage'))

// Map route keys to their page components
const routeComponents: Record<string, React.LazyExoticComponent<React.ComponentType>> = {
  home: HomePage,
  about: AboutPage,
  products: ProductsPage,
  sustainability: SustainabilityPage,
  news: NewsPage,
  contacts: ContactsPage,
  cookie: CookiePolicyPage,
  privacy: PrivacyPage,
  whistleblowing: WhistleblowingPage,
}

/** Minimal loading fallback — a dark screen with the accent bar to avoid flash */
function LoadingFallback() {
  return (
    <div className="min-h-screen bg-dark-deep flex items-center justify-center">
      <div className="h-[2px] w-16 bg-primary animate-pulse" />
    </div>
  )
}

function ScrollToTop() {
  const { pathname } = useLocation()
  useEffect(() => {
    window.scrollTo(0, 0)
  }, [pathname])
  return null
}

function LanguageWrapper() {
  const { lang } = useParams<{ lang: string }>()
  const { i18n } = useTranslation()

  useEffect(() => {
    if (lang && supportedLanguages.includes(lang as SupportedLanguage)) {
      if (i18n.language !== lang) {
        i18n.changeLanguage(lang)
      }
    }
  }, [lang, i18n])

  // Redirect invalid language to default
  if (!lang || !supportedLanguages.includes(lang as SupportedLanguage)) {
    return <Navigate to={`/${defaultLanguage}`} replace />
  }

  return (
    <>
      <ScrollToTop />
      <Header />
      <Suspense fallback={<LoadingFallback />}>
        <Outlet />
      </Suspense>
    </>
  )
}

/**
 * Generate all localized route elements.
 * For each route key, we create Route elements for both IT and EN slugs.
 * The home route (empty slug) uses `index`.
 */
function buildLocalizedRoutes() {
  const routes: React.ReactElement[] = []
  const seenPaths = new Set<string>()

  for (const [key, slugs] of Object.entries(localizedRoutes)) {
    const Component = routeComponents[key]
    if (!Component) continue

    // Collect unique path slugs for this route
    const uniqueSlugs = new Set([slugs.it, slugs.en])

    for (const slug of uniqueSlugs) {
      if (slug === '') {
        // Home route — use index
        if (!seenPaths.has('__index__')) {
          seenPaths.add('__index__')
          routes.push(<Route key={`${key}-index`} index element={<Component />} />)
        }
      } else if (!seenPaths.has(slug)) {
        seenPaths.add(slug)
        routes.push(<Route key={`${key}-${slug}`} path={slug} element={<Component />} />)
      }
    }
  }

  return routes
}

export function LanguageRouter() {
  return (
    <Routes>
      {/* Redirect root to default language */}
      <Route path="/" element={<Navigate to={`/${defaultLanguage}`} replace />} />

      {/* Language-prefixed routes */}
      <Route path="/:lang" element={<LanguageWrapper />}>
        {buildLocalizedRoutes()}

        {/* News detail — dynamic slug route */}
        <Route path="news/:slug" element={<NewsDetailPage />} />

        {/* Catch-all 404 */}
        <Route path="*" element={<NotFoundPage />} />
      </Route>
    </Routes>
  )
}
